import { useCallback, useEffect, useState } from "react";
import {
  listQcChecklist,
  setQcOrgFailureNotes,
  setQcOrgFailurePhotos,
  type QcChecklistCategory,
  type QcChecklistItem,
} from "@/lib/manufacturing/builderAssistantChecklistClient";
import { BuilderAssistantChecklistCard } from "./BuilderAssistantChecklistCard";
import { BuilderAssistantChecklistNew } from "./BuilderAssistantChecklistNew";
import { BuilderAssistantQcRoster } from "./BuilderAssistantQcRoster";

export function BuilderAssistantChecklistEditor() {
  const [categories, setCategories] = useState<QcChecklistCategory[]>([]);
  const [items, setItems] = useState<QcChecklistItem[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [failurePhotosRequired, setFailurePhotosRequired] = useState(false);
  const [failureNotesRequired, setFailureNotesRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const res = await listQcChecklist();
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setError(null);
    setCategories(res.data.categories);
    setItems(res.data.items);
    setIsOwner(res.data.is_owner);
    setFailurePhotosRequired(res.data.failure_photos_required);
    setFailureNotesRequired(res.data.failure_notes_required);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const visibleCategories = categories.filter((row) => row.status !== "archived");

  async function run(task: Promise<{ ok: boolean; error?: string }>) {
    setError(null);
    const res = await task;
    if (!res.ok) {
      setError(res.error ?? "Could not update the checklist.");
      return;
    }
    await reload();
  }

  return (
    <section className="space-y-4 text-sm">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">QC checklist</h3>
        <p className="text-gray-600 dark:text-zinc-400">
          Categories such as Floor and Walls, and the items checked on every walk. An owner makes a
          promoted item permanent and is the one who removes an item. Example pictures are added on the item.
        </p>
      </div>
      {error ? <p className="text-red-700 dark:text-red-300">{error}</p> : null}
      <BuilderAssistantQcRoster />
      <BuilderAssistantChecklistNew
        categories={visibleCategories.map((row) => ({ id: row.id, name: row.name }))}
        onSaved={reload}
      />
      {isOwner ? (
        <>
          <label className="flex items-center gap-2 text-gray-700 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={failurePhotosRequired}
              onChange={(event) => void run(setQcOrgFailurePhotos(event.target.checked))}
            />
            Require a photo on every failure
          </label>
          <label className="flex items-center gap-2 text-gray-700 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={failureNotesRequired}
              onChange={(event) => void run(setQcOrgFailureNotes(event.target.checked))}
            />
            Require a description on every failure
          </label>
        </>
      ) : null}
      {visibleCategories.map((category) => (
        <BuilderAssistantChecklistCard
          key={category.id}
          category={category}
          items={items}
          isOwner={isOwner}
          notesLocked={failureNotesRequired}
          categoryIds={visibleCategories.map((row) => row.id)}
          categories={visibleCategories}
          mode={openId === category.id ? "open" : confirmId === category.id ? "confirm" : "closed"}
          onRequestOpen={() => {
            setOpenId(null);
            setConfirmId(category.id);
          }}
          onConfirmOpen={() => {
            setConfirmId(null);
            setOpenId(category.id);
          }}
          onCancel={() => setConfirmId(null)}
          onClose={() => setOpenId(null)}
          run={(task) => void run(task)}
        />
      ))}
    </section>
  );
}
